import { getApiUrl } from './graphql-utils';
import { getAccessToken } from './storage';
import { expireSession, refreshSession } from './token-refresh-link';

/**
 * Socket des subscriptions. L'API l'authentifie à l'ouverture et la ferme (4403) à l'échéance de son token. On la
 * rouvre un peu avant, et après chaque refresh HTTP ; `connectionParams` relit le token à chaque (re)connexion et le
 * rafraîchit s'il expire, puis graphql-ws relance les abonnements actifs. Un événement émis pendant la reconnexion
 * peut se perdre : c'est accepté.
 */

// Rouverte une minute avant l'échéance : la fermeture par l'API reste un filet de sécurité, pas le cas nominal.
const RESTART_AHEAD_MS = 60_000;
// Recette « restartable client » de graphql-ws : fermeture demandée par le client.
const CLIENT_RESTART_CLOSE_CODE = 4205;
// Fermetures attendues, après lesquelles on rouvre : refus ou échéance du token (4403), redémarrage demandé ici
// (4205) ou par `terminate()` (4499), arrêt ou redémarrage du serveur, coupure réseau. Le reste signale un bug.
const RETRYABLE_CLOSE_CODES = [1001, 1006, 1012, 1013, 1014, 4403, CLIENT_RESTART_CLOSE_CODE, 4499];
const MAX_RETRY_WAIT_MS = 30_000;

let activeSocket: WebSocket | null = null;
let restartTimer: ReturnType<typeof setTimeout> | null = null;

/** Échéance d'un access token, lue dans son `exp`. Pas de vérification : c'est l'API qui en juge. */
export function getTokenExpiryMs(token: string | null): number | null {
    try {
        const payload = token?.split('.')[1];
        if (!payload) return null;
        const json = atob(
            payload
                .replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(Math.ceil(payload.length / 4) * 4, '='),
        );
        const { exp } = JSON.parse(json) as { exp?: number };
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
}

function clearRestartTimer(): void {
    if (restartTimer) clearTimeout(restartTimer);
    restartTimer = null;
}

/** Rouvre la socket : graphql-ws se reconnecte avec le token courant et relance les abonnements actifs. */
export function restartSocket(): void {
    if (activeSocket?.readyState === WebSocket.OPEN) {
        activeSocket.close(CLIENT_RESTART_CLOSE_CODE, 'Client Restart');
    }
}

export function onSocketConnected(socket: unknown): void {
    activeSocket = socket as WebSocket;
    clearRestartTimer();
    // Un token déjà dans la dernière minute (refresh raté à l'ouverture) n'est pas redémarré en boucle : l'API fermera
    // la socket à son échéance, et la reconnexion retentera le refresh.
    const expiresAtMs = getTokenExpiryMs(getAccessToken());
    const delayMs = expiresAtMs ? expiresAtMs - RESTART_AHEAD_MS - Date.now() : 0;
    if (delayMs > 0) restartTimer = setTimeout(restartSocket, delayMs);
}

export function onSocketClosed(): void {
    activeSocket = null;
    clearRestartTimer();
}

export function shouldRetrySocket(errOrCloseEvent: unknown): boolean {
    const code = (errOrCloseEvent as { code?: unknown } | null)?.code;
    return typeof code !== 'number' || RETRYABLE_CLOSE_CODES.includes(code);
}

// Attente exponentielle plafonnée : celle de graphql-ws n'a pas de plafond, et les tentatives sont illimitées.
export async function waitBeforeSocketRetry(retries: number): Promise<void> {
    const delayMs = Math.min(1_000 * 2 ** retries, MAX_RETRY_WAIT_MS) + Math.random() * 1_000;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * Paramètres de chaque (re)connexion : un token expiré, ou qui le sera dans la minute, est d'abord rafraîchi. Refresh
 * token mort : on déconnecte et on lève, la socket s'arrête au lieu de boucler sur des refus. Refresh sans réponse
 * (réseau) : on garde le token courant, l'API le refuse s'il a expiré et graphql-ws réessaie.
 *
 * `refreshesToken` dit à l'API que ce client rafraîchit son token avant de rouvrir : elle peut fermer la socket à
 * l'échéance du token. Sans lui, elle applique la tolérance des anciens bundles.
 */
export async function getSocketConnectionParams(): Promise<{ token: string | null; refreshesToken: true }> {
    const expiresAtMs = getTokenExpiryMs(getAccessToken());
    if (expiresAtMs && expiresAtMs - Date.now() < RESTART_AHEAD_MS) {
        const result = await refreshSession(getApiUrl()).catch(() => null);
        if (result?.type === 'session_expired') {
            expireSession(result.reason);
            throw new Error('Session expired');
        }
    }
    return { token: getAccessToken(), refreshesToken: true };
}
